import { useMemo } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import UIList from 'UIComponents/list/UIList';
import ObjectListName from 'automation-ui-references/customer-data/ObjectListName';
import EmailName from 'automation-ui-references/customer-data/EmailName';
import FormName from 'automation-ui-references/customer-data/FormName';
import IntegrationName from 'automation-ui-references/customer-data/IntegrationName';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import TeamName from 'automation-ui-references/customer-data/TeamName';
import UserName from 'automation-ui-references/customer-data/UserName';
import WorkflowName from 'automation-ui-references/customer-data/WorkflowName';
import FeedbackSurveyName from 'automation-ui-references/customer-data/FeedbackSurveyName';
import { OUTBOUND_ASSET_TYPES, OUTBOUND_REFERENCE_TYPES } from 'automation-ui-management/constants/WorkflowReferenceConstants';
import { CLASSIC, PLATFORM } from 'automation-ui-flow-core/flow/FlowSourceTypes';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OutboundFlowReferencesSection = ({
  outboundReferences
}) => {
  const crmUsageOutboundReferences = useMemo(() => outboundReferences.filter(ref => ref.outboundReferenceType === OUTBOUND_REFERENCE_TYPES.CRM_USAGE), [outboundReferences]);
  const trackEvent = useTrackEvent();
  const trackAccordionChange = (isOpen, key) => {
    if (isOpen) {
      trackEvent(Trackables.VIEWED_REFERENCE_TYPE, {
        assetType: key,
        referenceType: 'outbound'
      });
    }
  };
  const renderExpandableReferenceType = (refs, title, key, styled = true) => {
    if (!refs.length) {
      return /*#__PURE__*/_jsx(View, {
        marginInlineStart: "400",
        marginBlockEnd: "200",
        as: "div",
        children: title
      });
    }
    return /*#__PURE__*/_jsx(UIAccordionItem, {
      onOpenChange: event => trackAccordionChange(event.target.value, key),
      size: "extra-small",
      title: title,
      children: /*#__PURE__*/_jsx(UIList, {
        styled: styled,
        children: refs
      })
    }, key);
  };
  const renderListLinks = refs => refs.filter(ref => OUTBOUND_REFERENCE_TYPES.LIST === ref.outboundReferenceType).map((ref, index) => /*#__PURE__*/_jsx(ObjectListName, {
    id: ref.id
  }, `${ref.id}-${index}`));
  const renderEmailLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.EMAIL === ref.assetType).map((ref, index) => /*#__PURE__*/_jsx(EmailName, {
    id: ref.id,
    href: ref.referenceUrl,
    disabled: false
  }, `${ref.id}-${index}`));
  const renderFlowLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.AUTOMATION_PLATFORM_FLOW === ref.assetType || OUTBOUND_ASSET_TYPES.WORKFLOW === ref.assetType).map((ref, index) => /*#__PURE__*/_jsx(WorkflowName, {
    flowSourceType: ref.assetType === OUTBOUND_ASSET_TYPES.AUTOMATION_PLATFORM_FLOW ? PLATFORM : CLASSIC,
    id: ref.id,
    href: ref.referenceUrl
  }, `${ref.id}-${index}`));
  const renderFormLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.FORM === ref.assetType).map((ref, index) => /*#__PURE__*/_jsx(FormName, {
    id: ref.id,
    href: ref.referenceUrl
  }, `${ref.id}-${index}`));
  const renderIntegrationLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.INTEGRATION === ref.assetType).map((ref, index) => /*#__PURE__*/_jsx(IntegrationName, {
    id: ref.id,
    href: ref.referenceUrl || undefined
  }, `${ref.id}-${index}`));
  const renderPropertyLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.PROPERTY === ref.assetType).map((ref, index) => {
    const atts = ref.id.split('/');
    return /*#__PURE__*/_jsx(PropertyName, {
      objectTypeId: atts[0],
      propertyName: atts[1],
      href: ref.referenceUrl
    }, `${ref.id}-${index}`);
  });
  const renderTeamLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.TEAM === ref.assetType).map((ref, index) => /*#__PURE__*/_jsx(TeamName, {
    id: ref.id,
    href: ref.referenceUrl
  }, `${ref.id}-${index}`));
  const renderUserLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.USER === ref.assetType).map((ref, index) => /*#__PURE__*/_jsx(UserName, {
    id: ref.id,
    href: ref.referenceUrl
  }, `${ref.id}-${index}`));
  const renderFeedbackSurveyLinks = refs => refs.filter(ref => OUTBOUND_ASSET_TYPES.FEEDBACK_SURVEY === ref.assetType).map((ref, index) => /*#__PURE__*/_jsx(FeedbackSurveyName, {
    surveyReferenceId: ref.id,
    href: ref.referenceUrl
  }, `${ref.id}-${index}`));
  const listLinks = renderListLinks(outboundReferences);
  const emailReferences = renderEmailLinks(crmUsageOutboundReferences);
  const formReferences = renderFormLinks(crmUsageOutboundReferences);
  const flowReferences = renderFlowLinks(crmUsageOutboundReferences);
  const integrationReferences = renderIntegrationLinks(crmUsageOutboundReferences);
  const propertyReferences = renderPropertyLinks(crmUsageOutboundReferences);
  const teamReferences = renderTeamLinks(crmUsageOutboundReferences);
  const userReferences = renderUserLinks(crmUsageOutboundReferences);
  const feedbackSurveyReferences = renderFeedbackSurveyLinks(crmUsageOutboundReferences);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "outbound-flow-references-section",
    children: [renderExpandableReferenceType(listLinks, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.lists",
      options: {
        count: listLinks.length
      }
    }), OUTBOUND_REFERENCE_TYPES.LIST), renderExpandableReferenceType(emailReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.emails",
      options: {
        count: emailReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.EMAIL), renderExpandableReferenceType(formReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.forms",
      options: {
        count: formReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.FORM), renderExpandableReferenceType(flowReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.workflows",
      options: {
        count: flowReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.AUTOMATION_PLATFORM_FLOW), renderExpandableReferenceType(integrationReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.integrations",
      options: {
        count: integrationReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.INTEGRATION), renderExpandableReferenceType(propertyReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.properties",
      options: {
        count: propertyReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.PROPERTY), renderExpandableReferenceType(teamReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.teams",
      options: {
        count: teamReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.TEAM), renderExpandableReferenceType(userReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.users",
      options: {
        count: userReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.USER), renderExpandableReferenceType(feedbackSurveyReferences, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.feedbackSurveys",
      options: {
        count: feedbackSurveyReferences.length
      }
    }), OUTBOUND_ASSET_TYPES.FEEDBACK_SURVEY)]
  });
};
export default OutboundFlowReferencesSection;